import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { JsErrorEntry } from '../types/js-error-entry';
import { RedactionService } from '../../security/redaction.service';
import { RouteMaskService } from '../../security/route-mask.service';
import { SessionTimelineCollector } from './session-timeline.collector';
import { RingBuffer } from '../storage/ring-buffer';

@Injectable({
  providedIn: 'root',
})
export class ErrorCaptureCollector {
  private readonly entries = new RingBuffer<JsErrorEntry>(50);
  private started = false;

  constructor(
    private readonly router: Router,
    private readonly sessionTimeline: SessionTimelineCollector,
    private readonly redactionService: RedactionService,
    private readonly routeMaskService: RouteMaskService,
  ) {}

  private readonly onError = (event: ErrorEvent): void => {
    const route = this.getCurrentRoute();
    const message = this.redactionService.redactText(event.message || event.error?.message || 'Unknown error');
    const stack = this.redactionService.redactText(event.error?.stack);

    this.entries.push({
      kind: 'error',
      message,
      stack,
      source: event.filename,
      line: event.lineno,
      column: event.colno,
      route,
      timestamp: new Date().toISOString(),
    });

    this.sessionTimeline.recordError(message, route, stack);
  };

  private readonly onUnhandledRejection = (event: PromiseRejectionEvent): void => {
    const route = this.getCurrentRoute();
    const reason = event.reason;
    const message = this.redactionService.redactText(reason?.message ?? reason ?? 'Unhandled promise rejection'); // i18n-ignore: diagnostic data
    const stack = this.redactionService.redactText(reason?.stack);

    this.entries.push({
      kind: 'unhandledrejection',
      message,
      stack,
      route,
      timestamp: new Date().toISOString(),
    });

    this.sessionTimeline.recordError(message, route, stack);
  };

  start(): void {
    if (this.started) {
      return;
    }

    this.started = true;
    window.addEventListener('error', this.onError);
    window.addEventListener('unhandledrejection', this.onUnhandledRejection);
  }

  stop(): void {
    if (!this.started) {
      return;
    }

    this.started = false;
    window.removeEventListener('error', this.onError);
    window.removeEventListener('unhandledrejection', this.onUnhandledRejection);
  }

  clear(): void {
    this.entries.clear();
  }

  getEntries(): readonly JsErrorEntry[] {
    return this.entries.toArray();
  }

  private getCurrentRoute(): string {
    return this.routeMaskService.normalizeRoute(
      this.router.url || window.location.pathname + window.location.search + window.location.hash,
    );
  }
}
